import { z } from 'zod';
/**
 * 給定 FormSchema，產出對應 Zod schema 校驗 FormInstanceData。
 * 遍歷所有 sections 的 fields，根據 field 類型生成對應的 Zod validator。
 *
 * @param schema 表單模板 schema
 * @param opts.mode 'strict'（默认）或 'partial'。详见 ValidatorMode 文档
 */
export function jsonSchemaToZod(schema, opts = {}) {
    const mode = opts.mode ?? 'strict';
    const shape = {};
    // 兜底：schema.sections 缺失視為空表單，產出空 z.object({})，後續 parse 任意空對象都通過。
    for (const section of schema.sections ?? []) {
        for (const f of section.fields) {
            shape[f.id] = fieldToZod(f, mode);
        }
    }
    return z.object(shape);
}
/**
 * 將單一 FormField 轉換為對應的 Zod schema。
 * - strict 模式：required 字段返回基本類型；非 required 字段返回 optional().nullable()
 * - partial 模式：所有字段（不論 required 與否）都返回 optional().nullable()，
 *   但 base 校验（type / enum / max / min 等）仍生效
 */
function fieldToZod(f, mode = 'strict') {
    let base;
    switch (f.type) {
        case 'text':
        case 'textarea':
            base = z.string().max(f.maxLength ?? 10_000);
            break;
        case 'number':
            base = z
                .number()
                .min(f.min ?? Number.NEGATIVE_INFINITY)
                .max(f.max ?? Number.POSITIVE_INFINITY);
            break;
        case 'date':
        case 'datetime':
            // 2026-09-25 回報序49：日期欄接受 ISO datetime（DatePicker 產出）與純日期 YYYY-MM-DD
            // （後端預填 / 匯入常用格式）；舊版只收 datetime，預填的純日期讓草稿永遠存不了。
            base = z.union([z.string().datetime({ offset: true }), z.string().regex(DATE_ONLY_RE)]);
            break;
        case 'select':
        case 'radio':
            base = z.enum(f.options.map((o) => o.value));
            if ('allowOther' in f && f.allowOther)
                base = z.union([base, z.string()]);
            break;
        case 'multiselect':
            base = z.array(z.enum(f.options.map((o) => o.value)));
            break;
        case 'checkbox':
            base = z.boolean();
            break;
        case 'file':
        case 'photo':
            base = f.multiple ? z.array(z.number().int()) : z.number().int();
            break;
        case 'signature':
            base = z.number().int();
            break;
        case 'table':
            base = z.array(z.record(z.string(), z.unknown()));
            break;
        case 'dailySchedule':
            // 多日行程（序74）：[{ date: 'YYYY-MM-DD' | '', rows: [{colId: value}] }]
            base = z.array(z.object({
                date: z.string().max(40).nullable().optional(),
                rows: z.array(z.record(z.string(), z.unknown())).optional().default([]),
            }));
            break;
        case 'checklist': {
            const clauseShape = z.object({
                result: z.enum(['yes', 'no', 'na']).nullable().optional().default(null),
                note: z.string().max(2000).optional().default(''),
                attachments: z.array(z.number().int()).optional().default([]),
            });
            base = z.record(z.string(), clauseShape);
            break;
        }
    }
    // table / checklist 類型沒有 required 屬性，其他類型透過 'required' in f 安全訪問
    const isRequired = 'required' in f && f.required;
    // 序49：草稿（partial）隨時可存——所有欄位 optional+nullable，且容忍「尚未填完」的中間值
    // （空字串 / 數字欄的數字字串）；非空的非法 enum / 非數字文字仍拒絕以保護資料完整性。
    if (mode === 'partial')
        return tolerateDraftValue(f, base).optional().nullable();
    // strict：非必填欄位留空（null / 缺省 / 空字串）一律視為未填，不因格式校驗擋提交
    if (!isRequired)
        return withEmptyString(f, base).optional().nullable();
    return base;
}
/** 純日期 YYYY-MM-DD */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
/** 數字字串（草稿中數字欄可能暫存為字串，如 '1.5'） */
const NUMERIC_STRING_RE = /^\s*-?\d+(\.\d+)?\s*$/;
/** 會出現「空字串＝未填」的標量欄位類型 */
const EMPTY_STRING_TOLERANT_TYPES = new Set([
    'number',
    'date',
    'datetime',
    'select',
    'radio',
    'signature',
    'file',
    'photo',
]);
/**
 * 標量欄位額外接受空字串（視為未填）；其餘類型原樣返回。
 *
 * @param f    欄位定義
 * @param base 欄位基本校驗
 */
function withEmptyString(f, base) {
    return EMPTY_STRING_TOLERANT_TYPES.has(f.type) ? z.union([base, z.literal('')]) : base;
}
/**
 * 草稿容忍：在 withEmptyString 之上，數字欄另接受數字字串。
 *
 * @param f    欄位定義
 * @param base 欄位基本校驗
 */
function tolerateDraftValue(f, base) {
    const tolerant = withEmptyString(f, base);
    return f.type === 'number' ? z.union([tolerant, z.string().regex(NUMERIC_STRING_RE)]) : tolerant;
}
//# sourceMappingURL=validators.js.map